import EventDetails from "@/components/widgets/dashboard/calendar/eventModal/content/EventDetails";
import EventInvitation from "@/components/widgets/dashboard/calendar/eventModal/content/EventInvitation";
import AgendaItem from "@/domain/entities/agenda/AgendaItem";
import type UserProfile from "@/domain/entities/user/UserProfile";
import { cn, isEventPast } from "@/lib/utils";
import routes from "@/router/routes";
import { closeAssistant } from "@/stores/assistantStore";
import { focusEventInCalendar } from "@/stores/eventStore";
import { CalendarDays } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

/** « 08:30 – 09:30 », or « Toute la journée ». */
export const eventTimeRange = (
    event: AgendaItem,
    language: string,
    allDay: string,
): string => {
    if (event.isAllDay) return allDay;
    const hour = (date: Date) =>
        new Date(date).toLocaleTimeString(language, {
            hour: "2-digit",
            minute: "2-digit",
        });

    return `${hour(event.start)} – ${hour(event.end)}`;
};

/** An invitation still to come: not organised by the user, neither cancelled nor past. */
export const canAnswerInvitation = (
    event: AgendaItem,
    user: UserProfile | undefined,
) =>
    !!user &&
    !event.userIsOrganizer(user) &&
    !event.isCancelled &&
    !isEventPast(event);

interface EventActionsProps {
    event: AgendaItem;
    canAnswer: boolean;
    onAnswered: (event: AgendaItem) => void;
    className?: string;
}

export const EventActions = ({
    event,
    canAnswer,
    onAnswered,
    className,
}: EventActionsProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    if (!canAnswer && !event.id) return null;

    const openInCalendar = () => {
        closeAssistant();
        focusEventInCalendar(event);
        navigate(routes.dashboard);
    };

    return (
        <div
            className={cn(
                "flex flex-wrap items-center justify-between gap-2",
                className,
            )}
        >
            {!!event.id && (
                <button
                    className="flex w-fit items-center gap-1.5 text-[13px] font-semibold text-primary-dark hover:underline"
                    onClick={openInCalendar}
                    type="button"
                >
                    <CalendarDays aria-hidden className="h-3.5 w-3.5" />
                    {t("vistassistant.openInCalendar")}
                </button>
            )}
            {canAnswer && (
                <EventInvitation
                    className="ml-auto p-0"
                    compact
                    setUpdatedEvent={(updated) =>
                        onAnswered(updated as AgendaItem)
                    }
                    updatedEvent={event}
                />
            )}
        </div>
    );
};

export const fullDetailsClassName = "[&>div]:max-h-none";

interface EventDetailsBlockProps {
    event: AgendaItem;
    /** Accept or decline shown (canAnswerInvitation). */
    canAnswer: boolean;
    /** The event answered: the new state to keep. */
    onAnswered: (event: AgendaItem) => void;
    /** The details scroll past a height (in a line of the chat). */
    scrollDetails?: boolean;
}

/**
 * The details of an event (attendees, description, meeting link), then on one row the link to the
 * calendar and accept or decline for an invitation still to come.
 */
const EventDetailsBlock = ({
    event,
    canAnswer,
    onAnswered,
    scrollDetails = false,
}: EventDetailsBlockProps) => (
    <>
        <div
            className={cn(
                fullDetailsClassName,
                scrollDetails && "max-h-[320px] overflow-y-auto",
            )}
        >
            <EventDetails event={event} />
        </div>
        <EventActions
            canAnswer={canAnswer}
            event={event}
            onAnswered={onAnswered}
        />
    </>
);

export default EventDetailsBlock;
